import type { WebWidgetModel } from '@web/types';

import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { map, type Observable, Subject, take } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { AppFacade } from '@web/store/app/app.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { ToolbarWidgetComponent } from '@web/shared/components/web-toolbar-widget/web-toolbar-widget.component';
import { WebWidgetGroupPipe } from '@web/shared/pipes/widget-group.pipe';

@Component({
    selector: 'web-add-zero-widget',
    templateUrl: './add-zero-widget.component.html',
    styleUrls: ['./add-zero-widget.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AsyncPipe, TranslatePipe, ToolbarWidgetComponent, WebWidgetGroupPipe],
})
export class AddZeroWidgetComponent {
    @Output() public addWidget: EventEmitter<WebWidgetModel> = new EventEmitter<WebWidgetModel>();

    private readonly bsModalRef: BsModalRef = inject(BsModalRef);
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly widgetsService = inject(WebWidgetsService);

    public readonly isAdmin$: Observable<boolean> = this.appFacade.isAdmin$;
    public readonly isEduSite$: Observable<boolean> = this.pagesFacade.isEduSite$;
    public readonly eduUserTariff$: Observable<string> = this.userFacade.eduUserTariff$;

    public onClose: Subject<WebWidgetModel> = new Subject<WebWidgetModel>();

    public isWidgetDisabled(widget: WebWidgetModel) {
        return this.isAdmin$.pipe(
            take(1),
            map((isAdmin) => {
                return !isAdmin && (widget.params.disabled || !widget.permission.isReleased);
            })
        );
    }

    public onAddWidget(widget: WebWidgetModel): void {
        this.isWidgetDisabled(widget).subscribe((disabled) => {
            if (disabled) {
                return;
            }

            this.onClose.next(widget);
            this.bsModalRef.hide();
        });
    }

    public get widgets$(): Observable<WebWidgetModel[]> {
        return this.widgetsService.getZeroWidgets$();
    }

    public onCancel(): void {
        this.onClose.next(null);
        this.bsModalRef.hide();
    }
}
